import React, {memo, useMemo, useState,} from "react";
import {useTranslation} from "react-i18next";
import {Map} from "ol";
import {Point} from "ol/geom";
import VectorSource from "ol/source/Vector";
import {useCluster} from "@delivus-daas/react-open-layers";
import Feature from "ol/Feature";
import {SelectEvent} from "ol/interaction/Select";
import {Button} from "shared/ui";
import {MarkerSvg} from "shared/assets/svgs/MarkerSvg";
import {CLUSTER_GROUP_KEY, clusterOverStyle, clusterStyle} from "shared/lib/map.fns";
import {IPlanLongItem, IShippingWithCluster} from "../../types";
import {isUpdateDisabled} from "shared/lib/fns";

interface Props {
  map?: Map;
  zoom: number;
  selectedPlans: IPlanLongItem[];
  onClickCluster: (selected: Feature[]) => void;
  onMouseOverCluster: (selected: Feature[], deselected: Feature[], event: SelectEvent) => void;
  onInit: (cluster: VectorSource) => void;
}

export const MarkerClusterButton = memo(({
                                           map, selectedPlans, zoom, onInit, onClickCluster, onMouseOverCluster
                                         }: Props) => {
  const {t} = useTranslation(["shipping"]);
  const [isVisibleMarker, setVisibleMarker] = useState(true);
  
  const features = useMemo(() => {
    const nextFeatures: Feature[] = [];
    
    selectedPlans.forEach((cluster) => {
      cluster.shippingitems_geo?.forEach((shipping) => {
        const shippingWithCluster: IShippingWithCluster = {
          ...shipping, cluster_id: cluster.cluster_id, id: cluster.id
        };
        const disabled = isUpdateDisabled(shipping.shipping_item__status, shipping.is_scanned);
        
        nextFeatures.push(new Feature({
          [CLUSTER_GROUP_KEY]: cluster.cluster_id, geometry: new Point([
            shipping.shipping_item__address__lng, shipping.shipping_item__address__lat,
          ]), shipping: shippingWithCluster, disabled,
        }));
      });
    });
    
    return nextFeatures;
  }, [selectedPlans]);
  
  useCluster({
    features,
    map,
    zoom,
    visible: isVisibleMarker,
    onInit,
    onClick: onClickCluster,
    onOver: onMouseOverCluster,
    clusterStyle: clusterStyle,
    overStyle: clusterOverStyle,
    layerOptions: {zIndex: 15}
  });
  
  return (<Button
    variant={"primary"}
    size={"sm"}
    className={"button " + isVisibleMarker}
    onClick={() => setVisibleMarker((prev) => !prev)}>
    <MarkerSvg className={isVisibleMarker ? "text-white" : "text-black/85"} />
    {t("marker layer")}
  </Button>);
});
